import React, { useState } from "react";
import { FaHourglassHalf } from "react-icons/fa";
import TaxIdChangeModal from "./TaxIdChangeModal";
import { requestPartyTaxIdChange, uploadTaxIdDocument } from "../helpers/apiHelper";
import {
  canRequestPartyTaxIdChange,
  currentTaxIdValue,
  formatDayMonth,
  getPendingTaxIdChange,
  getTaxIdChangeMatters,
  isTaxIdChangeEnabled,
  resolveTaxIdType,
} from "../helpers/taxIdChangeHelper";
import "../styles/TaxIdChangeModal.css";

export const PendingTaxIdBadge = ({ pending, t }) => {
  const date = formatDayMonth(pending?.requestedAt);
  const fullLabel = date
    ? t("taxIdChange.pendingBadge").replace("{date}", date)
    : t("taxIdChange.pendingBadgeNoDate");
  const shortLabel = date
    ? t("taxIdChange.pendingBadgeShort").replace("{date}", date)
    : t("taxIdChange.pendingBadgeShortNoDate");
  return (
    <span className="tax-id-pending-badge" data-testid="tax-id-pending-badge" title={fullLabel}>
      <FaHourglassHalf aria-hidden="true" />
      <span aria-hidden="true">{shortLabel}</span>
      <span className="sr-only">{fullLabel}</span>
    </span>
  );
};

/**
 * "Solicitar correção" link (or the matter page shortcut) + pending badge + the request modal.
 * `party` is the GET /clients/parties/{id} response; `onRefresh` reloads it silently after a request.
 */
const TaxIdChangeAction = ({
  party,
  partyId,
  features,
  token,
  author,
  t,
  matterId,
  matterName,
  variant = "inline",
  onRefresh,
  onApplied,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [localPending, setLocalPending] = useState(null);

  if (!party || !isTaxIdChangeEnabled(features)) return null;

  const details = party.details || {};
  const type = resolveTaxIdType(details);
  const pending = localPending || getPendingTaxIdChange(party);
  const canRequest = !localPending && canRequestPartyTaxIdChange(features, party, { forbidden });
  const matters = getTaxIdChangeMatters(party, matterId ? { id: matterId, name: matterName || matterId } : null);

  // Keep the modal mounted while open, even if the request just made the action unavailable.
  if (!pending && !canRequest && !isOpen) return null;

  const submit = (payload, { signal }) => requestPartyTaxIdChange(partyId, payload, token, { signal });
  const uploadFile = (file, targetMatterId) => uploadTaxIdDocument({ token, file, matterId: targetMatterId, author });

  const handleResult = (result) => {
    if (result.status === "APPLIED") {
      onApplied?.(result);
    } else {
      setLocalPending({ type: result.type, requestedAt: new Date().toISOString(), dueDate: result.dueDate });
    }
    onRefresh?.();
  };

  const handleErrorCode = (code) => {
    if (code === "FORBIDDEN") setForbidden(true);
    if (code === "REQUEST_PENDING") setLocalPending({ type, requestedAt: null });
    if (code === "REQUEST_PENDING" || code === "TIMEOUT") onRefresh?.();
  };

  const label = variant === "shortcut"
    ? t("taxIdChange.matterShortcut").replace("{type}", type)
    : t("taxIdChange.requestButton");

  return (
    <span className="tax-id-inline" data-testid={`tax-id-action-${variant}`}>
      {pending && !isOpen ? (
        <PendingTaxIdBadge pending={pending} t={t} />
      ) : (
        <button
          type="button"
          className="tax-id-request-link"
          onClick={() => setIsOpen(true)}
          aria-label={variant === "shortcut" ? undefined : t("taxIdChange.requestButtonAria").replace("{type}", type)}
          aria-haspopup="dialog"
          data-testid={variant === "shortcut" ? "tax-id-matter-shortcut" : "tax-id-request-button"}
          disabled={Boolean(pending)}
        >
          {label}
        </button>
      )}
      {isOpen && (
        <TaxIdChangeModal
          isOpen
          onClose={() => setIsOpen(false)}
          t={t}
          type={type}
          currentValue={currentTaxIdValue(details, type)}
          matters={matters}
          initialMatterId={matterId}
          submit={submit}
          uploadFile={uploadFile}
          onResult={handleResult}
          onErrorCode={handleErrorCode}
        />
      )}
    </span>
  );
};

export default TaxIdChangeAction;
